import { useCanShowBrandedLogo } from '@/components/decoration/mspLogoHelper';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useModal } from '@/components/modal/useModal';
import { cn } from '@/lib/utils';
import { PurchaseOrderBundle } from '@/services/api/contracts/catalog/bundles';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { useGetLogo } from '@/services/api/hooks/images';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { PoStatus } from '@/services/purchaseOrders/usePurchaseOrderService';
import { useCurrentCustomerRegion } from '@/services/region/useCurrentRegion';
import { Menu } from '@headlessui/react';
import { pdf, UsePDFInstance } from '@react-pdf/renderer';
import { FC, useRef, useState } from 'react';
import { FiMenu } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { useBuildPurchaseOrderCompleteDocumentTableData } from './complete/productTable/useBuildPurchaseOrderCompleteDocumentTableData';
import PurchaseOrderCompleteDocument from './complete/PurchaseOrderCompleteDocument';
import DeletePurchaseOrderModal from './DeletePurchaseOrderModal';
import DuplicatePurchaseOrderModal from './DuplicatePurchaseOrderModal';
import { usePurchaseOrderDetails } from './usePurchaseOrderDetails';

interface Props {
  order: PurchaseOrderBundle;
  submitted?: boolean;
}

export const PurchaseOrderActionsMenu: FC<Props> = ({ order, submitted: completed }) => {
  const navigate = useNavigate();

  // TODO update once status available
  // const isDraftOrder = order.status === PoStatus.draft;
  const isDraftOrder = true;

  const deleteModalProps = useModal<{ purchaseOrder: PurchaseOrderBundle }>();
  const duplicateModalProps = useModal<{ purchaseOrderId: string }>();

  const ref = useRef<HTMLButtonElement>(null);
  const [showAbove, setShowAbove] = useState<boolean>(false);

  const calculatePosition = () => {
    const windowHeight = window.innerHeight;

    const buttonRect = ref.current?.getBoundingClientRect();
    const buttonTop = buttonRect?.y;

    setShowAbove(buttonTop ? buttonTop > windowHeight * 0.6 : false);
  };

  const onDownloadPdf = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, instance: UsePDFInstance) => {
    if (instance.loading || !instance.url) {
      return;
    }
    const link = document.createElement('a');
    link.href = instance.url;
    link.download = `${order.name}.pdf`;
    link.click();

    // Issue with normal PDF download when in action menu. Need to prevent its trigger.
    event.stopPropagation();
    event.preventDefault();
  };

  const { totalCost, totalDeviceFee, totalPcmCost, deviceFeeItem } = usePurchaseOrderDetails(order);

  const { partnerId, companyId } = useParams();
  if (!(partnerId && companyId)) throw new Error('Missing params');

  const [mspDetails] = useMspDetailsForCustomer(partnerId, companyId);

  const [customer] = useCompany(partnerId, companyId);

  const tableData = useBuildPurchaseOrderCompleteDocumentTableData(order);

  const currentRegion = useCurrentCustomerRegion();

  const role = useRole();

  // TODO check if this condition is necessary.
  const canShowBrandedLogo = useCanShowBrandedLogo() || role.type === RoleType.Admin;
  const [logoUrl] = useGetLogo(partnerId);

  const onPdfClick = async () => {
    const blob = await pdf(
      <PurchaseOrderCompleteDocument
        order={order}
        selectedDeviceFee={deviceFeeItem}
        data={tableData}
        totalPrice={totalCost}
        totalPcm={totalPcmCost || 0}
        totalDeviceFee={totalDeviceFee || 0}
        msp={mspDetails}
        customer={customer}
        regionCode={currentRegion?.code}
        brandLogoUrl={canShowBrandedLogo && logoUrl ? logoUrl : null}
      />
    ).toBlob();

    // Create a Blob URL
    const url = URL.createObjectURL(blob);

    // Open the URL in a new tab
    window.open(url, '_blank');
  };

  return (
    <>
      <Menu>
        <Menu.Button
          as="button"
          className="flex items-center justify-center gap-2"
          ref={ref}
          onClick={calculatePosition}
        >
          <div className="relative">
            <div className="flex items-center justify-center rounded-full px-5">
              <FiMenu />
            </div>
            <Menu.Items
              className={twMerge(
                'absolute z-50 right-0 mt-2 min-w-fit w-[200px] divide-y',
                'divide-gray-100 rounded-md border border-gray-200 bg-white',
                'shadow-lg outline-none',
                showAbove ? ' bottom-[110%]' : 'top-[100%]'
              )}
            >
              <div>
                {!completed && (
                  <Menu.Item>
                    <Button
                      variant={ButtonVariant.Mono}
                      onClick={() => navigate(`${order.id}/poCatalogue/selectedProducts`)}
                      className={cn('rounded-none border-0 w-full', !isDraftOrder && 'h-full')}
                    >
                      {isDraftOrder ? 'Resume Order' : 'Review Order'}
                    </Button>
                  </Menu.Item>
                )}
                {completed && (
                  <Menu.Item>
                    <Button
                      variant={ButtonVariant.Mono}
                      onClick={() => navigate(`${order.id}/complete`)}
                      className={cn('rounded-none border-0 w-full')}
                    >
                      View Details
                    </Button>
                  </Menu.Item>
                )}
                <Menu.Item>
                  <Button
                    variant={ButtonVariant.Mono}
                    className={cn('rounded-none border-0 w-full')}
                    onClick={onPdfClick}
                  >
                    {order.state === PoStatus.Draft ? 'Generate Quote' : 'Generate PDF'}
                  </Button>
                </Menu.Item>
                <Menu.Item>
                  <Button
                    variant={ButtonVariant.Mono}
                    onClick={() => {
                      duplicateModalProps.open({ purchaseOrderId: order.id! });
                    }}
                    className="rounded-none border-0 w-full"
                  >
                    Duplicate Order
                  </Button>
                </Menu.Item>
                {!completed && (
                  <Menu.Item>
                    <Button
                      variant={ButtonVariant.DesctructiveGhost}
                      onClick={() => {
                        deleteModalProps.open({ purchaseOrder: order });
                      }}
                      className="rounded-none border-0 w-full"
                    >
                      Delete Order
                    </Button>
                  </Menu.Item>
                )}
              </div>
            </Menu.Items>
          </div>
        </Menu.Button>
      </Menu>
      <DeletePurchaseOrderModal {...deleteModalProps} />
      <DuplicatePurchaseOrderModal {...duplicateModalProps} />
    </>
  );
};
